import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  IonCol,
  IonGrid,
  IonHeader,
  IonImg,
  IonItemDivider,
  IonRow,
  isPlatform,
} from '@ionic/react';
import { DniReader } from '@pepper/capacitor-dni-reader';
import classNames from 'classnames';
import { idniecap } from 'idniecap';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ModalClose from '@/assets/icons/close.svg';
import CaretLeftIcon from '@/assets/imgs/arrow-left.svg';
import PepperLogo from '@/assets/imgs/pepper-logo.svg';
import { ClientTaxonomyProps } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { ManagedModalRoutes } from '@/interfaces/ManagedModalInterface/ManagedModalInterface';
import { OcrNfcRequest } from '@/interfaces/OcrInterface/OcrInterface';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';
import { saveVersionControl } from '@/redux/configOptions/config';
import {
  clearBudgetSimulations,
  clearSimulationTerm,
} from '@/redux/orderManaged/orderManaged';
import { PostOrder } from '@/redux/orderManaged/orderManagedInterface';
import { Analytics } from '@/services/analytics';
// import { setAnalyticsUserProperties, ClientTaxonomyProps } from '@/services/analytics';
import { requestVersionControl } from '@/services/api/version';
import InsightService from '@/services/insights';
import { storageService } from '@/services/storage';
import hubInstance from '@/services/websocket';
import { RootState } from '@/store';

import Quit from '../EDniModal/Auxiliaries/Quit/Quit';
import Denial from './Auxiliaries/Denial/Denial';
import Timeout from './Auxiliaries/Timeout/Timeout';
import Transition from './Auxiliaries/Transition/Transition';
import UnknownError from './Auxiliaries/UnknownError/UnknownError';
import LocalizedBugetOrderOrder from './Budget/LocalizedBudget';
import ConfirmedCustomerShipment from './ConfirmedCustomerShipment/ConfirmedCustomerShipment';
import CustomerAccess from './CustomerAccess/CustomerAccess';
import { DniScanManaged } from './DniScan/DniScan';
import EntryManaged from './entry/EntryManaged';
import styles from './OperationManaged.module.scss';
import OrderAlreadyAssociated from './Order/OrderAlreadyAssociated';
import PersonalData from './OrderPersonalData/OrderPersonalData';
import { ShippingClientManaged } from './ShippingClientManaged/ShippingClientManaged';
import ShippingFinishedManaged from './ShippingFinishedManaged/ShippingFinishedManaged';

interface OperationMangedProps {
  cartId?: string;
  onDismiss: () => void;
}
export default function OperationManged({
  cartId,
  onDismiss,
}: OperationMangedProps) {
  const dispatch = useDispatch();
  const managedFrame = useRef<any>(null);
  const [modalRoute, setModalRoute] = useState<ManagedModalRoutes>(
    ManagedModalRoutes.ENTRY_MANAGED,
  );
  const [quitScreen, setQuitScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [managedUrl, setManagedUrl] = useState<string>('');
  const [managedLoaded, setManagedLoaded] = useState<boolean>(false);
  const [showBackButton, setShowBackButton] = useState<boolean>(true);
  const [showCloseButton, setShowCloseButton] = useState<boolean>(true);
  const [isEmail, setIsEmail] = useState<boolean>(false);
  const [isDocument, setIsDocument] = useState<boolean>(false);
  const [orderInvoiceData, setOrderInvoiceData] = useState<
  PostOrder | undefined
  >(undefined);
  const [preferredOffer, setPreferredOffer] = useState<boolean>(false);
  const [selectedSelePreferredOffer, setSelectedPreferredOffer] = useState<boolean>(false);
  const isApp = Capacitor.isNativePlatform();

  useEffect(() => {
    InsightService.logTrace('ManagedPage', [{ page: modalRoute }]);
    sessionStorage.getItem('retaken-eco')
    && modalRoute === ManagedModalRoutes.LOCALIZED_BUDGET_ORDER
      ? setShowBackButton(false)
      : setShowBackButton(true);
  }, [modalRoute]);

  const routesWithBackButton = [
    ManagedModalRoutes.ORDER_PERSONAL_DATA,
    ManagedModalRoutes.LOCALIZED_ORDER,
    ManagedModalRoutes.ORDER_ALREADY_ASSOCIATED,
    ManagedModalRoutes.CONFIRMED_CUSTOMER_SHIPMENT,
    ManagedModalRoutes.LOCALIZED_BUDGET_ORDER,
    ManagedModalRoutes.DniSCAN_MANAGED,
    // ManagedModalRoutes.SHIPPING_CLIENT_MANAGED
  ];

  const routesWithCloseButton = [
    ManagedModalRoutes.UNKNOWN_ERROR,
    ManagedModalRoutes.DENIAL,
    ManagedModalRoutes.TIMEOUT,
    ManagedModalRoutes.SHIPPING_FINISHED_MANAGED,
  ];
  // nfc
  const [superExpressFlow] = useState(storageService.hasSuperExpress);
  const [versionControl, setVersionControl] = useState<VersionControl>();
  const [appVersion, setAppVersion] = useState('');
  const [isNFCSupported, setIsNFCSupported] = useState(false);
  const [ocrData, setOcrData] = useState<OcrNfcRequest | undefined>(undefined);
  const versionControlData = useSelector(
    (state: RootState) => state.configSlice.versionControl,
  );
  if (!isPlatform('mobileweb') && !isPlatform('desktop')) {
    App?.getInfo().then((version) => {
      setAppVersion(version.version);
    });
  }
  const checkDefaultValues = async () => {
    let superExpressValue = false;
    if (!versionControlData) {
      await requestVersionControl().then((res) => {
        // If it's a native platform, we take the value from versionControl file
        // Else, the superExpress value is false
        superExpressValue = Capacitor.isNativePlatform()
          ? (res as VersionControl).super_express_default
          : false;
        const versionRes = {
          ...(res as VersionControl),
          super_express_default: superExpressValue,
        };
        setVersionControl(versionRes);
        // defaultSuperExpress = versionRes.super_express_default;
        dispatch(saveVersionControl(versionRes));
      });
    } else {
      setVersionControl(versionControlData);
      superExpressValue = Capacitor.isNativePlatform()
        ? versionControlData.super_express_default
        : false;
    }
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.SUPER_EXPRESS_ENABLED]: (superExpressFlow !== null
        ? superExpressFlow
        : superExpressValue
      ).toString(),
    });
  };
  useEffect(() => {
    if (DniReader && Capacitor.isNativePlatform() && isPlatform('android')) {
      DniReader.isSupported().then((result) => {
        setIsNFCSupported(result.isSupported);
      });
    } else if (isPlatform('ios')) {
      idniecap.isNFCEnable().then((result) => {
        if (result.disponible) {
          setIsNFCSupported(true);
        }
      });
    }
    checkDefaultValues();
  }, []);
  const getPreviousRoute = (
    currentRoute: ManagedModalRoutes,
  ): ManagedModalRoutes | null => {
    switch (currentRoute) {
      case ManagedModalRoutes.ORDER_PERSONAL_DATA:
        if (
          appVersion
          && (superExpressFlow !== null
            ? superExpressFlow
            : versionControl?.super_express_default)
          && isNFCSupported
        ) {
          return ManagedModalRoutes.DniSCAN_MANAGED;
        }
        return ManagedModalRoutes.LOCALIZED_BUDGET_ORDER;

      case ManagedModalRoutes.LOCALIZED_ORDER:
        return ManagedModalRoutes.ENTRY_MANAGED;
      case ManagedModalRoutes.ORDER_ALREADY_ASSOCIATED:
        return ManagedModalRoutes.ENTRY_MANAGED;
      case ManagedModalRoutes.CONFIRMED_CUSTOMER_SHIPMENT:
        return ManagedModalRoutes.SHIPPING_CLIENT_MANAGED;
      // case ManagedModalRoutes.SHIPPING_CLIENT_MANAGED:
      // return ManagedModalRoutes.ORDER_PERSONAL_DATA;
      case ManagedModalRoutes.LOCALIZED_BUDGET_ORDER:
        return ManagedModalRoutes.ENTRY_MANAGED;
      case ManagedModalRoutes.DniSCAN_MANAGED:
        return ManagedModalRoutes.LOCALIZED_BUDGET_ORDER;
      default:
        return null;
    }
  };

  const handleDismiss = () => {
    if (modalRoute === ManagedModalRoutes.SHIPPING_FINISHED_MANAGED) {
      hubInstance.disconnect();
    }
    if (modalRoute === ManagedModalRoutes.ENTRY_MANAGED) {
      hubInstance.disconnect();
    }
    dispatch(clearBudgetSimulations());
    dispatch(clearSimulationTerm());
    onDismiss();
  };

  const selectRoute = (page?: ManagedModalRoutes) => {
    switch (page) {
      case ManagedModalRoutes.ENTRY_MANAGED:
        return (
          <EntryManaged
            cartIdParam={cartId}
            nextStep={(
              route: ManagedModalRoutes,
              isDocument?: boolean,
              isPreferred?: boolean,
            ) => {
              setModalRoute(route);
              setIsDocument(isDocument!);
              setPreferredOffer(
                isPreferred === undefined ? false : isPreferred,
              );
            }}
            handleLoading={(e: boolean) => setIsLoading(e)}
            // sendIFrameUrl={(url: string) => setManagedUrl(url)}
          />
        );
      case ManagedModalRoutes.DniSCAN_MANAGED:
        return (
          <DniScanManaged
            nextStep={(
              route: ManagedModalRoutes,
              isDocument?: boolean,
              orderInvoiceData?: PostOrder,
              nfcData?: OcrNfcRequest,
            ) => {
              setModalRoute(route);
              setIsDocument(isDocument!);
              setOrderInvoiceData(orderInvoiceData);
              setOcrData(nfcData);
            }}
            handleLoading={(e: boolean) => setIsLoading(e)}
            isDocument={isDocument}
            // sendIFrameUrl={(url: string) => setManagedUrl(url)}
          />
        );

      case ManagedModalRoutes.ORDER_PERSONAL_DATA:
        return (
          <PersonalData
            nextStep={(
              route: ManagedModalRoutes,
              orderInvoiceData?: PostOrder,
            ) => {
              setModalRoute(route);
              setOrderInvoiceData(orderInvoiceData);
            }}
            sendIFrameUrl={(url: string) => setManagedUrl(`${url}${isApp ? '&is_app=true' : ''}`)}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
            isDocument={isDocument}
            personalDataOcrData={ocrData}
          />
        );

      case ManagedModalRoutes.LOCALIZED_BUDGET_ORDER:
        return (
          <LocalizedBugetOrderOrder
            nextStep={(
              route: ManagedModalRoutes,
              isDocument?: boolean,
              isPreferredSelected?: boolean,
            ) => {
              setModalRoute(route);
              setIsDocument(isDocument!);
              setSelectedPreferredOffer(isPreferredSelected!);
            }}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
            isDocument={isDocument}
            isPreferredOffer={preferredOffer}
            selectedPreferredOffer={selectedSelePreferredOffer}
          />
        );
      case ManagedModalRoutes.ORDER_ALREADY_ASSOCIATED:
        return (
          <OrderAlreadyAssociated
            nextStep={(route: ManagedModalRoutes) => setModalRoute(route)}
            sendIFrameUrl={(url: string) => setManagedUrl(`${url}${isApp ? '&is_app=true' : ''}`)}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
          />
        );
      case ManagedModalRoutes.SHIPPING_CLIENT_MANAGED:
        return (
          <ShippingClientManaged
            nextStep={(route: ManagedModalRoutes, isEmail: boolean) => {
              setModalRoute(route);
              setIsEmail(isEmail);
            }}
            sendIFrameUrl={(url: string) => setManagedUrl(`${url}${isApp ? '&is_app=true' : ''}`)}
            handleLoading={(e: boolean) => setIsLoading(e)}
            orderInvoiceData={orderInvoiceData}
          />
        );
      case ManagedModalRoutes.CONFIRMED_CUSTOMER_SHIPMENT:
        return (
          <ConfirmedCustomerShipment
            nextStep={(route: ManagedModalRoutes) => setModalRoute(route)}
            handleLoading={(e: boolean) => setIsLoading(e)}
            isEmail={isEmail}
          />
        );
      case ManagedModalRoutes.SHIPPING_FINISHED_MANAGED:
        return (
          <ShippingFinishedManaged
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
            handleClose={() => handleDismiss()}
          />
        );
      case ManagedModalRoutes.CUSTOMER_ACCESS:
        return (
          <CustomerAccess
            nextStep={(route: ManagedModalRoutes) => setModalRoute(route)}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
          />
        );
      case ManagedModalRoutes.UNKNOWN_ERROR:
        return <UnknownError handleClose={() => handleDismiss()} />;
      case ManagedModalRoutes.DENIAL:
        return <Denial handleClose={() => handleDismiss()} />;
      case ManagedModalRoutes.TIMEOUT:
        return <Timeout handleClose={() => handleDismiss()} />;
      default:
        break;
    }
  };
  return (
    <div
      className={classNames(styles.pepperManagedModal, styles.managedModal)}
      id="pepper-managed-modal"
    >
      {!managedLoaded && (
        <>
          <IonItemDivider className={styles.pepperModalHeaderWrapper}>
            <IonHeader className={styles.pepperModalHeader}>
              <IonGrid>
                <IonRow>
                  <IonCol className={styles.headerBackWrapper}>
                    {showBackButton
                      && routesWithBackButton.includes(modalRoute) && (
                        <IonImg
                          src={CaretLeftIcon}
                          className={styles.headerCloseBtn}
                          onClick={() => {
                            const previousRoute = getPreviousRoute(modalRoute);
                            if (previousRoute) {
                              setModalRoute(previousRoute);
                              if (
                                previousRoute
                                === ManagedModalRoutes.ENTRY_MANAGED
                              ) {
                                dispatch(clearBudgetSimulations());
                                dispatch(clearSimulationTerm());
                              }
                            }
                          }}
                        />
                    )}
                  </IonCol>
                  <IonCol>
                    <IonImg
                      src={PepperLogo}
                      className={styles.pepperHeaderLogo}
                    />
                  </IonCol>
                  <IonCol className={styles.headerCloseWrapper}>
                    {showCloseButton
                      && !routesWithCloseButton.includes(modalRoute) && (
                        <IonImg
                          src={ModalClose}
                          className={styles.headerCloseBtn}
                          onClick={() => {
                            setQuitScreen(true);
                            setShowCloseButton(false);
                            setShowBackButton(false);
                          }}
                        />
                    )}
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonHeader>
          </IonItemDivider>
          {isLoading && <Transition />}
          <div className={styles.pepperModalBody}>
            {quitScreen ? (
              <Quit
                handleReturn={() => {
                  setQuitScreen(false);
                  setShowCloseButton(true);
                  setShowBackButton(true);
                  setShowCloseButton(true);
                }}
                handleClose={() => handleDismiss()}
              />
            ) : (
              <>{selectRoute(modalRoute)}</>
            )}
          </div>
        </>
      )}
      {managedUrl && (
        <div style={{ height: '100%' }}>
          <iframe
            ref={managedFrame}
            src={managedUrl}
            className={styles.pepperIframe}
            title="ecommerce"
            allow="microphone; camera; midi; encrypted-media;"
            width="100%"
            height="100%"
            id="pepper-ecommerce-iframe"
            onLoad={() => {
              setIsLoading(false);
              setManagedLoaded(true);
            }}
          />
        </div>
      )}
    </div>
  );
}
